/**
 * Ritmo da fábrica: ambientes terminados por dia útil (decisão 3 do Igor:
 * "ritmo real recente", automático).
 *
 * Por que semanas −28 a −3: o término é lançado com 1 a 4 semanas de atraso
 * (plano 2.8), então as semanas 0, −1 e −2 ainda vão "encher" e baixariam o
 * ritmo. As 26 semanas fechadas antes delas foram a régua que previu a fila
 * com erro de −8% a +19% na verificação.
 */
import {
  anosDoIntervalo,
  contarDiasUteis,
  inicioDaSemana,
  maiorIso,
  somarDiasCorridos,
} from "./calendario";

export const COBERTURA_MINIMA = 0.9;
export const LIMITE_DERIVA = 0.2;

export const MOTIVO_RITMO = Object.freeze({
  HISTORICO_INSUFICIENTE: "historico_insuficiente",
  CALENDARIO_AUSENTE: "calendario_ausente",
});

const JANELA = { primeira: -28, ultima: -3 };
const JANELA_RECENTE = { primeira: -10, ultima: -3 };
const SEMANAS_PROVISORIAS = 2; // −1 e −2, além da semana corrente

/** Semanas de segunda a domingo; semana 0 = a de `hoje`. */
function intervaloDeSemanas(hoje, primeira, ultima) {
  const segunda = inicioDaSemana(hoje);
  return {
    de: somarDiasCorridos(segunda, primeira * 7),
    ate: somarDiasCorridos(segunda, (ultima + 1) * 7 - 1),
  };
}

/** Janela do ritmo (26 semanas fechadas) relativa a `hoje`. */
export function janelaDoRitmo(hoje) {
  return intervaloDeSemanas(hoje, JANELA.primeira, JANELA.ultima);
}

/** Semanas 0, −1 e −2: términos aí ainda são provisórios. */
export function intervaloProvisorio(hoje) {
  return { de: somarDiasCorridos(inicioDaSemana(hoje), -7 * SEMANAS_PROVISORIAS), ate: hoje };
}

/**
 * Anos de [de, ate] sem nenhum registro no calendário da unidade. Sem
 * calendário o "dia útil" do ano é chute (faltariam feriados e recessos),
 * então o ritmo daquele ano não é calculado (plano 5.6).
 */
export function anosSemCalendario(de, ate, anosComRegistro) {
  const registrados = new Set([...(anosComRegistro || [])].map(Number));
  return anosDoIntervalo(de, ate).filter((ano) => !registrados.has(ano));
}

function contarNoTrecho(datas, de, ate) {
  return datas.filter((data) => data >= de && data <= ate).length;
}

function taxaNoTrecho(datas, de, ate, naoUteis) {
  if (ate < de) return { taxa: null, du: 0, quantidade: 0 };
  const du = contarDiasUteis(de, ate, naoUteis);
  const quantidade = contarNoTrecho(datas, de, ate);
  return { taxa: du > 0 ? quantidade / du : null, du, quantidade };
}

function menorData(datas) {
  return datas.reduce((menor, data) => (menor === null || data < menor ? data : menor), null);
}

// Um mês só "tem dado" se o ritmo dele for pelo menos 1/4 do ritmo mediano
// da janela. O menor término isolado não serve como começo do histórico: a
// planilha do ano novo traz uns poucos términos de out/nov do ano anterior
// (~11% do mediano), e com eles a cobertura dava 100% com o ritmo 26% abaixo
// do real (verificação com a cópia de 08/05/26). Com 1/2 o recesso de fim de
// ano (início de janeiro a ~38% do mediano, sem o recesso cadastrado) cortava
// o histórico e a página ficava sem ritmo todo janeiro.
export const DENSIDADE_MINIMA_DO_MES = 0.25;
// Trecho de mês com menos dias úteis que isso (borda da janela) é curto
// demais para medir ritmo: vale como coberto só se tiver algum término.
const DU_MINIMO_DO_MES = 5;

function trechosMensais(de, ate) {
  const trechos = [];
  let inicio = de;
  while (inicio <= ate) {
    const [ano, mes] = inicio.split("-").map(Number);
    const ultimoDia = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
    const fimDoMes = `${inicio.slice(0, 8)}${String(ultimoDia).padStart(2, "0")}`;
    trechos.push({ de: inicio, ate: fimDoMes < ate ? fimDoMes : ate });
    inicio = somarDiasCorridos(fimDoMes, 1);
  }
  return trechos;
}

function mediana(valores) {
  const ordenados = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  return ordenados.length % 2 ? ordenados[meio] : (ordenados[meio - 1] + ordenados[meio]) / 2;
}

/**
 * Começo do histórico contínuo dentro da janela: percorre os meses do mais
 * recente para o mais antigo, para no primeiro mês ralo e devolve o primeiro
 * término do mês mais antigo aceito (dado que começa no meio do mês não
 * dilui o ritmo com os dias sem dado). `null` quando nem o mês mais recente
 * tem dado.
 */
export function inicioDoHistoricoContinuo(datas, janela, naoUteis) {
  const meses = trechosMensais(janela.de, janela.ate).map((trecho) => ({
    ...trecho,
    ...taxaNoTrecho(datas, trecho.de, trecho.ate, naoUteis),
  }));
  const taxas = meses.filter((m) => m.du >= DU_MINIMO_DO_MES && m.quantidade > 0).map((m) => m.taxa);
  if (taxas.length === 0) return null;
  const minima = mediana(taxas) * DENSIDADE_MINIMA_DO_MES;
  let inicio = null;
  for (let i = meses.length - 1; i >= 0; i -= 1) {
    const mes = meses[i];
    const temDado = mes.du < DU_MINIMO_DO_MES ? mes.quantidade > 0 : mes.taxa >= minima;
    if (!temDado) break;
    inicio = menorData(datas.filter((data) => data >= mes.de && data <= mes.ate)) || mes.de;
  }
  return inicio;
}

/**
 * `calcularRitmo({terminos, naoUteis, hoje, anosComRegistro?, horizonteAte?})`
 *
 * - `terminos`: `[{termino: "AAAA-MM-DD"}]` (a união de `unirTerminos`).
 * - `anosComRegistro`: anos com calendário cadastrado. Omitido = não checa.
 * - `horizonteAte`: último dia que a tela vai projetar (também precisa de
 *   calendário).
 *
 * Retorno: `{ritmo, motivo, cobertura, janela, coberta, duJanela, duCoberta,
 * terminosNaJanela, inicioDados, ritmo8, deriva, avisoDeriva, anos}`;
 * `ritmo` é `null` com `motivo` "calendario_ausente" (+ `anos`) ou
 * "historico_insuficiente" (+ `cobertura`). Nunca um número inventado.
 */
export function calcularRitmo({ terminos, naoUteis, hoje, anosComRegistro, horizonteAte } = {}) {
  const janela = janelaDoRitmo(hoje);
  const vazio = {
    ritmo: null,
    motivo: null,
    cobertura: 0,
    janela,
    coberta: null,
    duJanela: 0,
    duCoberta: 0,
    terminosNaJanela: 0,
    inicioDados: null,
    ritmo8: null,
    deriva: null,
    avisoDeriva: false,
    anos: [],
  };

  if (anosComRegistro !== undefined) {
    const fim = maiorIso(hoje, horizonteAte || hoje);
    const anos = anosSemCalendario(janela.de, fim, anosComRegistro);
    if (anos.length > 0) return { ...vazio, motivo: MOTIVO_RITMO.CALENDARIO_AUSENTE, anos };
  }

  const datas = (terminos || []).map((t) => t?.termino).filter(Boolean);
  const inicioDados = inicioDoHistoricoContinuo(datas, janela, naoUteis);
  const duJanela = contarDiasUteis(janela.de, janela.ate, naoUteis);
  const deCoberto = inicioDados ? maiorIso(janela.de, inicioDados) : null;
  const trecho = deCoberto ? taxaNoTrecho(datas, deCoberto, janela.ate, naoUteis) : { taxa: null, du: 0, quantidade: 0 };
  const cobertura = duJanela > 0 ? trecho.du / duJanela : 0;
  const base = {
    ...vazio,
    cobertura,
    duJanela,
    duCoberta: trecho.du,
    terminosNaJanela: trecho.quantidade,
    inicioDados,
    coberta: trecho.du > 0 ? { de: deCoberto, ate: janela.ate } : null,
  };
  // Ritmo zero (nenhum término em 26 semanas cobertas) não projeta fila:
  // tratado como histórico insuficiente em vez de prazo infinito.
  if (cobertura < COBERTURA_MINIMA || !trecho.taxa) {
    return { ...base, motivo: MOTIVO_RITMO.HISTORICO_INSUFICIENTE };
  }

  const recente = intervaloDeSemanas(hoje, JANELA_RECENTE.primeira, JANELA_RECENTE.ultima);
  const ritmo8 = taxaNoTrecho(datas, maiorIso(recente.de, deCoberto), recente.ate, naoUteis).taxa;
  const deriva = ritmo8 !== null && trecho.taxa > 0 ? ritmo8 / trecho.taxa - 1 : null;
  return {
    ...base,
    ritmo: trecho.taxa,
    ritmo8,
    deriva,
    avisoDeriva: deriva !== null && Math.abs(deriva) > LIMITE_DERIVA,
  };
}
